import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SOCIAL_ICON_PATHS } from '../shared/social-icons';

export interface SocialLink {
  network: string;
  // null : texte traduit (clé footer.socialEmail)
  label: string | null;
  // null : adresse pas encore renseignée dans le back office (logo affiché sans lien)
  url: string | null;
  // Classe primeicons du logo, ou tracé SVG (24×24) pour les réseaux absents de primeicons
  iconClass?: string;
  svgPath?: string;
}

// Réseaux du footer, dans l'ordre d'affichage
const NETWORKS: Omit<SocialLink, 'url'>[] = [
  { network: 'email', label: null, iconClass: 'pi pi-envelope' },
  { network: 'whatsapp', label: 'WhatsApp', iconClass: 'pi pi-whatsapp' },
  { network: 'facebook', label: 'Facebook', iconClass: 'pi pi-facebook' },
  // pi-twitter est le logo X depuis primeicons 7
  { network: 'x', label: 'X (Twitter)', iconClass: 'pi pi-twitter' },
  { network: 'youtube', label: 'YouTube', iconClass: 'pi pi-youtube' },
  { network: 'instagram', label: 'Instagram', iconClass: 'pi pi-instagram' },
  { network: 'linkedin', label: 'LinkedIn', iconClass: 'pi pi-linkedin' },
  { network: 'twitch', label: 'Twitch', iconClass: 'pi pi-twitch' },
  { network: 'tiktok', label: 'TikTok', iconClass: 'pi pi-tiktok' },
  { network: 'telegram', label: 'Telegram', iconClass: 'pi pi-telegram' },
  { network: 'pinterest', label: 'Pinterest', iconClass: 'pi pi-pinterest' },
  { network: 'threads', label: 'Threads', svgPath: SOCIAL_ICON_PATHS['threads'] },
  { network: 'bluesky', label: 'Bluesky', svgPath: SOCIAL_ICON_PATHS['bluesky'] },
];

// Adresses utilisées si l'API ne répond pas (rendu SSR, API arrêtée)
const FALLBACK: { network: string; url: string }[] = [
  { network: 'facebook', url: 'https://www.facebook.com/PromodeepCom' },
  { network: 'instagram', url: 'https://www.instagram.com/promodeepcom' },
  { network: 'x', url: 'https://x.com/PromodeepCom' },
  { network: 'youtube', url: 'https://www.youtube.com/channel/UCCa1NbA_FNZxT14zPUwbmGg' },
];

// Logos « Suivez-nous » du footer : tous les réseaux sont affichés, avec le lien renseigné dans le back office
// (Administration → Social networks), lu une fois par session (route publique GET /social-publishing/links)
@Injectable({
  providedIn: 'root'
})
export class SocialLinksService {

  readonly links$: Observable<SocialLink[]>;

  constructor(http: HttpClient) {
    this.links$ = http.get<{ network: string; url: string }[]>(environment.host + '/social-publishing/links').pipe(
      catchError(() => of(FALLBACK)),
      map(links => NETWORKS.map(network => {
        const url = links.find(link => link.network === network.network)?.url || '';
        const valid = network.network === 'email' ? /^mailto:[^\s]+$/.test(url) : /^https:\/\/[^\s]+$/.test(url);
        return { ...network, url: valid ? url : null };
      })),
      shareReplay(1),
    );
  }
}
